import { SearchFilterProps } from "@themuse/design-system";
import { GroupListItem } from "@themuse/design-system/lib/components/GroupList/GroupList";

import { useSearchHistory } from "hooks/SearchHistoryContext";
import { isMobile } from "./browser";
import {
    SearchCompany,
    SearchDispatch,
    FiltersActionTypes,
    UserInteractionActionTypes,
    SearchFilters
} from "../interfaces/context/searchContextInterfaces";
import { SearchFilterItemProps } from "../components/facets/SearchFacets";

export type FacetPropsFactory = (
    itemProps: SearchFilterItemProps,
) => SearchFilterProps;

export interface FacetProps {
    filters: SearchFilters,
    company?: SearchCompany,
    dispatch: SearchDispatch,
}

/**
 * Default values for radio type filters will most likely be an empty string,
 * but we want to use a more descriptive value for tracking.
 */
const defaultRadioValues = {
    radius: "Anywhere",
    postedDateRange: "Anytime"
};

/**
 * Returns a default value for radio type facets
 */
export const getRadioFacetValue = (field: string, value: string) => {
    if (value === "" && field in defaultRadioValues) {
        return defaultRadioValues[field];
    }
    return value;
};

export const getFilterItems = (
    filterItems: SearchFilterProps["items"],
    selectedFilters: string | string[],
    fieldType: string,
) => {
    const items = filterItems.map((item) => {
        const isNested = item && "items" in item && item?.items?.length;
        const defaultChecked = fieldType === "checkbox"
            ? (selectedFilters?.includes(item.value) ?? false)
            : selectedFilters === item.value;

        return {
            ...item,
            defaultChecked,
            items: isNested ? getFilterItems(item.items, selectedFilters, fieldType) : []
        };
    });
    return items;
};

const getFacetPropsOnChange = ({
    userInputFilterName,
    filters,
    dispatch
}) => ({
    onChange: (listItem) => {
        const { value: filterValue } = listItem;
        let updatedFilterValues: string[] = [];
        const selectedFilterValues: string[] = filters[userInputFilterName];
        if (selectedFilterValues) {
            if (selectedFilterValues.includes(filterValue)) {
                updatedFilterValues = selectedFilterValues.filter((value) => value && value !== filterValue);
            } else {
                updatedFilterValues = [...selectedFilterValues, filterValue];
            }
        } else {
            updatedFilterValues = [filterValue];
        }

        dispatch({
            type: FiltersActionTypes.updateFilter,
            payload: {
                [userInputFilterName]: updatedFilterValues
            }
        });
        dispatch({
            type: UserInteractionActionTypes.updateFilterInteraction,
            payload: {
                mobileClearAllClickedAt: null,
                desktopClearAllClickedAt: null,
                individualFilterClearedAt: null
            }
        });
    }
});

const getRadioFacetPropsOnChange = ({
    userInputFilterName,
    dispatch,
}) => ({
    onChange: (listItem) => {
        dispatch({
            type: FiltersActionTypes.updateFilter,
            payload: {
                [userInputFilterName]: listItem.value
            }
        });
        dispatch({
            type: UserInteractionActionTypes.updateFilterInteraction,
            payload: {
                individualFilterClearedAt: null,
                mobileClearAllClickedAt: null,
                desktopClearAllClickedAt: null
            }
        });
    }
});

/**
 * Get props for filters
 */
export const getFacetProps = (
    itemProps: SearchFilterItemProps & FacetProps
): SearchFilterProps => {
    const {
        disableOnCompanySelect = false,
        labelText,
        fieldType,
        defaultValue = "",
        searchFilterItems,
        userInputFilterName,
        filters,
        company,
        dispatch
    } = itemProps;
    const { addHistory } = useSearchHistory();
    const selectedFilters = filters[userInputFilterName];
    const filterItems = getFilterItems(searchFilterItems, selectedFilters, fieldType);
    const facetPropsCallbacks = fieldType === "checkbox" ? getFacetPropsOnChange({
        userInputFilterName,
        filters,
        dispatch
    }) : getRadioFacetPropsOnChange({
        userInputFilterName,
        dispatch
    });

    return {
        fieldType,
        items: filterItems,
        label: labelText,
        ...facetPropsCallbacks,
        onClear: () => {
            if (isMobile()) {
                dispatch({
                    type: UserInteractionActionTypes.updateFilterInteraction,
                    payload: {
                        individualFilterClearedAt: Date.now(),
                        mobileClearAllClickedAt: null,
                        desktopClearAllClickedAt: null
                    }
                });
                dispatch({
                    type: FiltersActionTypes.updateFilter,
                    payload: {
                        [userInputFilterName]: defaultValue || []
                    }
                });
            }
            addHistory();
        },
        onSave: () => {
            if (isMobile()) {
                dispatch({
                    type: UserInteractionActionTypes.updateFilterInteraction,
                    payload: {
                        individualFilterClearedAt: null,
                        mobileClearAllClickedAt: null,
                        desktopClearAllClickedAt: null
                    }
                });
            }
            addHistory();
        },
        ...(disableOnCompanySelect && { disabled: !!company?.userSelectedCompany?.value })
    };
};

/**
 * Returns selected filters count
 *
 * Remote checkbox updates the location reducer instead of the filters reducer,
 * so we need to check the remote_work_location_type value.
 *
 */
export const getSelectedFiltersCount = (filters: SearchFilters, remoteLocationType: string) => {
    let filterCount: number = (
        Object
            .values(filters)
            .reduce((accumulator: number, value: string | string[]) => {
                if (!Array.isArray(value) && !!value) {
                    return accumulator + 1;
                }
                if (Array.isArray(value)) {
                    return accumulator + value.length;
                }
                return accumulator;
            }, 0)
    );

    // Account for the remote checkbox in the filter count
    if (remoteLocationType === "virtual") {
        filterCount += 1;
    }

    return filterCount;
};

export const getFacetValuesFromFacetItems = (filterParamValue: string, facetItems: GroupListItem[]) => {
    const splitValues = decodeURIComponent(filterParamValue).split(",");
    return splitValues.map((elem) => {
        const facetItem = facetItems.find((item) => item.value.toLowerCase() === elem.toLowerCase());
        return (facetItem) ? facetItem.value : null;
    }).filter((elem) => elem !== null);
};
